/**
 * Service Wrapper
 *
 * Higher-order function that automatically wraps all service methods
 * with consistent error handling, eliminating the need for try/catch
 * blocks in individual service methods.
 */

import type { ApiError } from "./types";

/**
 * Wraps a service object with automatic error handling for all methods
 *
 * @param service - The raw service object with methods
 * @param domain - Domain name for logging (e.g., "billing", "settings")
 * @returns Proxied service with automatic error handling
 *
 * @example
 * ```ts
 * const rawService = {
 *   async getData() { return await apiClient.get("/data"); }
 * };
 * export const service = makeService(rawService, "data");
 * ```
 */
export function makeService<T extends Record<string, Function>>(
  service: T,
  domain: string,
): T {
  return new Proxy(service, {
    get(target, prop: string) {
      const fn = target[prop];
      if (typeof fn !== "function") return fn;

      return async (...args: any[]) => {
        try {
          return await fn.apply(target, args);
        } catch (error: any) {
          // Ignore cancellation errors - React Query handles these gracefully
          // DOMException with name "AbortError" is the standard way to signal cancellation
          if (
            (error instanceof DOMException && error.name === "AbortError") ||
            error.name === "AbortError"
          ) {
            // Re-throw cancellation errors as-is - React Query will handle them silently
            throw error;
          }

          // Extract error message from ApiError or generic error
          const apiError = error as ApiError;
          const message =
            apiError?.message ||
            apiError?.error ||
            error?.message ||
            `Failed to ${String(prop)
              .replace(/([A-Z])/g, " $1")
              .toLowerCase()}`;

          // Log error in development
          if (typeof window !== "undefined") {
            try {
              const viteEnv = (import.meta as any)?.env;
              if (viteEnv?.DEV || viteEnv?.MODE === "development") {
              }
            } catch {
            }
          }

          // Re-throw with user-friendly message
          const userError = new Error(message);
          // Preserve status code if available
          if (apiError?.statusCode) {
            (userError as any).statusCode = apiError.statusCode;
          }
          throw userError;
        }
      };
    },
  });
}
